// FO-003 (batch-12) — pure helpers voor de onboarding-startworkflow.
//
// Geen I/O, geen datum-library (CLAUDE.md). Dag-precisie in Europe/Amsterdam:
// een relatieve deadline = de NL-kalenderdag van de startdatum + N dagen,
// uitgedrukt als UTC-midnight van die NL-dag (zelfde conventie als de
// operationele-takenmodule, DST-veilig voor dag-vergelijk).

import type {
  CertificaatTypeStatus,
  OnboardingCertificaatDeadline,
  StartWorkflowCertStatus,
} from '@streep/types'
import { ONBOARDING_CERTIFICAAT_DEADLINE_DAGEN } from '@streep/types'
import { parseDagDatum, vandaagAmsterdam } from './dagtaken-datum'

/** NL-kalenderdag ('yyyy-mm-dd') van een UTC-instant. */
function nlYmd(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
}

/**
 * Relatieve deadline van een verplicht certificaat t.o.v. de startdatum.
 *
 * Resultaat = UTC-midnight van (NL-startdag + N dagen), of `null` als het
 * deadline-type `GEEN` is. WEEK_1 = +7, DRIE_MAANDEN = +90, TWAALF_MAANDEN =
 * +365 (FO-003 §11-keuze). Day-arithmetiek in UTC (geen DST), zodat het
 * vergelijken met `vandaagAmsterdam()` op dezelfde basis gebeurt.
 */
export function berekenCertificaatDeadline(
  startDatum: Date,
  deadlineType: OnboardingCertificaatDeadline,
): Date | null {
  const dagen = ONBOARDING_CERTIFICAAT_DEADLINE_DAGEN[deadlineType]
  if (dagen === null) return null
  const base = parseDagDatum(nlYmd(startDatum)) // UTC-midnight van de NL-startdag
  return new Date(base.getTime() + dagen * 86_400_000)
}

/**
 * Kalenderdagen (NL) tot een deadline t.o.v. nu. Negatief = verstreken,
 * 0 = vandaag, null als er geen deadline is. `now` injecteerbaar voor tests.
 */
export function dagenTotDeadline(
  deadline: Date | null,
  now: Date = new Date(),
): number | null {
  if (!deadline) return null
  const vandaag = vandaagAmsterdam(now)
  return Math.round((deadline.getTime() - vandaag.getTime()) / 86_400_000)
}

/**
 * Vat de 9 afgeleide certificaat-statussen (`bepaalTypeStatus`) samen tot de 6
 * cockpit-statussen. Eén bron zodat cockpit en cron dezelfde mapping delen.
 */
export function classificeerCertStatus(
  status: CertificaatTypeStatus,
): StartWorkflowCertStatus {
  switch (status) {
    case 'ACTIEF':
    case 'VERLOOPT_BINNENKORT':
      return 'HEEFT'
    case 'GEPLAND':
      return 'INGEPLAND'
    case 'IN_OPLEIDING':
      return 'IN_OPLEIDING'
    case 'WACHT_OP_CERTIFICAAT':
    // batch-54 certificaten (FO-408) — een zelf aangeleverd concept is "in
    // behandeling", net als wachten op het certificaat na een cursus: de stap
    // is niet klaar (certStatusKlaar false) maar ook niet ONTBREEKT, zodat de
    // startworkflow er geen rood signaal van maakt terwijl HR aan zet is.
    case 'WACHT_OP_CONTROLE':
      return 'WACHT'
    case 'NIET_VAN_TOEPASSING':
      return 'NVT'
    case 'VERLOPEN':
    case 'ONTBREEKT':
      return 'ONTBREEKT'
    default: {
      // Exhaustiveness-guard: een nieuwe status valt hier door.
      const _exhaustive: never = status
      return _exhaustive
    }
  }
}

/** Telt een cockpit-cert-status als "stap klaar" (heeft het cert, of n.v.t.)? */
export function certStatusKlaar(status: StartWorkflowCertStatus): boolean {
  return status === 'HEEFT' || status === 'NVT'
}

/**
 * Is een verplicht certificaat over zijn deadline én nog niet behaald? Alleen
 * dan een "verlopen"-signaal (FO-003: alleen signaleren, niet blokkeren). Een
 * ingeplande/lopende cursus telt niet als overschreden — die is in behandeling.
 */
export function certificaatOverschreden(
  status: StartWorkflowCertStatus,
  dagen: number | null,
): boolean {
  if (dagen === null) return false
  return dagen < 0 && status === 'ONTBREEKT'
}
